/**
 * React Query hooks for admin video operations
 * Admin-specific data fetching with React Query
 */

import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';
import { 
  fetchAllUsersVideos,
  fetchVideoUsers,
  fetchExtensionRequestUsers,
  fetchPinnedVideos,
  type VideoListParams,
  type VideoListResponse,
} from '@/api/videoApi';
import { videoKeys, adminKeys } from '../../../api/querry_keys';
import { useUserVideosWithPinned } from '../dashboard/useVideosQuery';

/**
 * Hook for fetching all users' videos (admin only)
 */
interface UseAllUsersVideosParams extends VideoListParams {
  isAdmin: boolean;
}

export const useAllUsersVideosInfinite = (params: UseAllUsersVideosParams) => {
  return useInfiniteQuery({
    // Include ALL filter parameters in query key for proper cache invalidation
    queryKey: [...videoKeys.admin(), 'infinite', {
      status: params.status,
      user_email: params.user_email,
      sort_by: params.sort_by,
      video_type: params.video_type,
      limit: params.limit,
    }],
    queryFn: async ({ pageParam = 0 }) => {
      const response = await fetchAllUsersVideos({
        ...params,
        offset: pageParam,
      });
      
      return response;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage: VideoListResponse) => {
      const nextOffset = (lastPage.offset || 0) + (lastPage.limit || 12);
      const hasMore = lastPage.has_more ?? false;
      const videosCount = lastPage.videos?.length || 0;
      const totalCount = lastPage.total_count || 0;
      const currentOffset = lastPage.offset || 0;
      
      // Additional safety check: if we've loaded all videos based on count
      const allVideosLoaded = currentOffset + videosCount >= totalCount;
      
      // Only continue if has_more is true AND we haven't loaded all videos
      return (hasMore && !allVideosLoaded) ? nextOffset : undefined;
    },
    enabled: params.isAdmin, // Only fetch if user is admin
    staleTime: 0, // Changed to 0 to allow immediate refetch on filter changes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
};

/**
 * Hook for fetching all users list (admin only)
 */
export const useVideoUsers = (isAdmin: boolean) => {
  return useQuery({
    queryKey: adminKeys.users(),
    queryFn: async () => {
      const response = await fetchVideoUsers();
      return response.users || [];
    },
    enabled: isAdmin,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
};

/**
 * Hook for fetching users who have submitted extension requests (admin only)
 */
export const useExtensionRequestUsers = (isAdmin: boolean) => {
  return useQuery({
    queryKey: ['extension-request-users'],
    queryFn: async () => {
      const response = await fetchExtensionRequestUsers();
      return response.users || [];
    },
    enabled: isAdmin,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
};


/**
 * Hook for fetching admin pinned videos with real-time updates
 * Pure React Query approach without persistent cache interference
 * 
 * CRITICAL: This query must be fresh to show real-time pin/unpin changes
 * for all admins and users. The staleTime is kept low to ensure cache
 * invalidation triggers a refetch quickly after pin/unpin events.
 */
export const useAdminPinnedVideos = () => {
  return useQuery({
    queryKey: videoKeys.pinnedVideos(true), // Admin = true for separate cache
    queryFn: async () => {
      const result = await fetchPinnedVideos(true); // Admin = true
      return result;
    },
    staleTime: 0, // Set to 0 to always refetch on invalidation
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes but allow immediate refetch
    refetchOnWindowFocus: true, // Allow refetching when window gains focus
    refetchOnMount: true, // Refetch when component mounts
  });
};

/**
 * Combined adapter hook for admin video operations
 * Maintains backward compatibility with existing admin components
 */
export const useAdminVideosAdapter = (params: UseAllUsersVideosParams) => {
  const queryClient = useQueryClient();
  const videosQuery = useAllUsersVideosInfinite(params);
  const usersQuery = useVideoUsers(params.isAdmin);
  const pinnedQuery = useAdminPinnedVideos();
  
  // We can reuse pin/unpin/delete from the user hooks since the API is the same
  const userVideosResult = useUserVideosWithPinned({
    userEmail: null, // Admin doesn't need user email filter
    limit: params.limit,
  });

  // Extract stable function references to avoid dependency issues
  const pinVideoFn = userVideosResult.pinVideo;
  const unpinVideoFn = userVideosResult.unpinVideo;
  const deleteVideoFn = userVideosResult.deleteVideo;

  // Store params in a ref to access in callbacks without causing re-renders
  const paramsRef = useRef(params);
  paramsRef.current = params;

  // Flatten pages into single array
  const videos = videosQuery.data?.pages.flatMap(page => page.videos) ?? [];
  const pinnedVideos = pinnedQuery.data ?? [];
  const allUsers = usersQuery.data ?? [];

  // Store videos and pinnedVideos in refs to access current values in callbacks
  const videosRef = useRef(videos);
  videosRef.current = videos;
  const pinnedVideosRef = useRef(pinnedVideos);
  pinnedVideosRef.current = pinnedVideos;
  
  // Get total counts from the first page
  const totalCount = videosQuery.data?.pages[0]?.total_count ?? 0;
  const totalUnfilteredCount = videosQuery.data?.pages[0]?.total_unfiltered_count ?? 0;

  // Create optimistic pin/unpin functions with immediate UI updates (no deps to avoid loops)
  const optimisticPinVideo = useCallback((videoId: string, userEmail: string) => {

    // Find video to pin (use ref to get current state)
    const videoToPin = videosRef.current.find((v: any) => v.id === videoId);

    if (videoToPin) {
      // Immediately update React Query cache for instant UI feedback (admin cache)
      queryClient.setQueryData(videoKeys.pinnedVideos(true), (old: any) => {
        const existingVideos = Array.isArray(old) ? old : [];

        // Only add if not already pinned
        if (!existingVideos.some((v: any) => v.id === videoId)) {
          const newPinnedVideo = {
            ...videoToPin,
            is_pinned: true,
            pinned_by: userEmail,
            pinned_at: new Date().toISOString()
          };
          const updatedVideos = [newPinnedVideo, ...existingVideos];
          return updatedVideos;
        }
        return existingVideos;
      });
    }

    // Then trigger API call in background
    // The WebSocket sync will handle refetching after backend confirms the change
    pinVideoFn(videoId, userEmail);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const optimisticUnpinVideo = useCallback((videoId: string) => {

    // Immediately update React Query cache for instant UI feedback (admin cache)
    queryClient.setQueryData(videoKeys.pinnedVideos(true), (old: any) => {
      const existingVideos = Array.isArray(old) ? old : [];
      const updatedVideos = existingVideos.filter((v: any) => v.id !== videoId);
      return updatedVideos;
    });

    // Find the userEmail for the API call (use refs to get current state)
    const video = videosRef.current.find((v: any) => v.id === videoId) || pinnedVideosRef.current.find((v: any) => v.id === videoId);

    if (video?.user_email) {
      // Then trigger API call in background
      // The WebSocket sync will handle refetching after backend confirms the change
      unpinVideoFn(videoId, video.user_email);
    } else {
      console.error('Could not find video or user_email for unpin operation', { videoId, videosCount: videosRef.current.length, pinnedVideosCount: pinnedVideosRef.current.length });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper to filter videos from a page
  const filterVideosFromPage = useCallback((page: any, videoId: string) => ({
    ...page,
    videos: page.videos.filter((v: any) => v.id !== videoId)
  }), []);

  // Helper to update infinite query cache by removing a video
  const removeVideoFromInfiniteQuery = useCallback((old: any, videoId: string) => {
    if (!old) return old;

    // Handle infinite query structure
    if (old.pages) {
      return {
        ...old,
        pages: old.pages.map((page: any) => filterVideosFromPage(page, videoId))
      };
    }

    // Handle array structure
    if (Array.isArray(old)) {
      return old.filter((v: any) => v.id !== videoId);
    }

    return old;
  }, [filterVideosFromPage]);

  const optimisticDeleteVideo = useCallback((videoId: string, userEmail: string) => {
    const currentParams = paramsRef.current;

    // Build the exact query key that's being used (must match useAllUsersVideosInfinite)
    const queryKey = [...videoKeys.admin(), 'infinite', {
      status: currentParams.status,
      user_email: currentParams.user_email,
      sort_by: currentParams.sort_by,
      video_type: currentParams.video_type,
      limit: currentParams.limit,
    }];

    // Immediately update React Query cache for instant UI feedback
    queryClient.setQueryData(queryKey, (old: any) => removeVideoFromInfiniteQuery(old, videoId));

    // Also remove from pinned videos if present
    queryClient.setQueryData(videoKeys.pinnedVideos(true), (old: any) => {
      const existingVideos = Array.isArray(old) ? old : [];
      return existingVideos.filter((v: any) => v.id !== videoId);
    });

    // Then trigger API call in background
    deleteVideoFn(videoId, userEmail);
  }, [queryClient, deleteVideoFn, removeVideoFromInfiniteQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Create stable callbacks for async operations (no deps to avoid infinite loops)
  const handleLoadMore = useCallback(async () => {
    if (videosQuery.hasNextPage && !videosQuery.isFetchingNextPage) {
      await videosQuery.fetchNextPage();
    }
  }, [videosQuery.hasNextPage, videosQuery.isFetchingNextPage, videosQuery.fetchNextPage]);

  const handleRefetch = useCallback(async () => {
    await videosQuery.refetch();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefetchPinnedVideos = useCallback(async () => {
    await pinnedQuery.refetch();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return useMemo(() => ({
    // Video data - videos come with public URLs directly from backend
    videos: videos,
    pinnedVideos: pinnedVideos,
    allUsers,
    
    // Loading states
    // Use isPending for initial load to prevent flash of empty state
    isLoading: videosQuery.isPending || videosQuery.isLoading,
    isLoadingMore: videosQuery.isFetchingNextPage,
    isLoadingUsers: usersQuery.isPending || usersQuery.isLoading,
    isLoadingPinnedVideos: pinnedQuery.isPending || pinnedQuery.isLoading,
    
    // Error states
    error: videosQuery.error ? String(videosQuery.error) : null,
    
    // Pagination
    hasMore: (() => {
      const hasNextPage = videosQuery.hasNextPage ?? false;
      return hasNextPage;
    })(),
    loadMore: handleLoadMore,
    
    // Counts
    totalCount,
    totalUnfilteredCount,
    
    // Refetch functions
    refetch: handleRefetch,
    refetchPinnedVideos: handleRefetchPinnedVideos,
    
    // Mutations (reused from user hooks)
    pinVideo: pinVideoFn,
    unpinVideo: unpinVideoFn,
    deleteVideo: deleteVideoFn,
    
    // Optimistic mutations (these call React Query mutations + local state updates)
    optimisticPinVideo,
    optimisticUnpinVideo,
    optimisticDeleteVideo,

    // Download helper
    handleDownloadLastFrame: async (signedUrl: string) => {
      const { downloadLastFrameFromUrl } = await import('@/utils/common/videoUtils');
      return downloadLastFrameFromUrl(signedUrl);
    },
    
    // Setter functions for compatibility (no-op in React Query)
    setVideos: () => {
    },
    setPinnedVideos: () => {
    },
  }), [
    videos,
    pinnedVideos,
    allUsers,
    videosQuery.isPending,
    videosQuery.isLoading,
    videosQuery.isFetchingNextPage,
    videosQuery.hasNextPage,
    videosQuery.error,
    usersQuery.isPending,
    usersQuery.isLoading,
    pinnedQuery.isPending,
    pinnedQuery.isLoading,
    totalCount,
    totalUnfilteredCount,
    handleLoadMore,
    handleRefetch,
    handleRefetchPinnedVideos,
    pinVideoFn,
    unpinVideoFn,
    deleteVideoFn,
    optimisticPinVideo,
    optimisticUnpinVideo,
    optimisticDeleteVideo,
  ]);
};
